import React, { useState, useEffect } from "react";
import { EDITORIAL, FONT_SIZE } from "@ds/tokens/tokens";
import { useGetIdentity, useLogout, useMenu, type TreeMenuItem } from "@refinedev/core";
import { useThemedLayoutContext } from "@refinedev/antd";
import type { ThemedLayout } from "@refinedev/antd";
import { Badge, Button, ConfigProvider, Layout, Menu, Space, theme, Typography } from "antd";
import type { MenuProps } from "antd";
import { Link } from "react-router";
import { useLogisticsAlertCount } from "../hooks/useLogisticsAlertCount";
import {
  GoogleOutlined, LeftOutlined, LogoutOutlined, RightOutlined,
  SearchOutlined, UserOutlined,
} from "@ant-design/icons";
import { GlobalSearchModal } from "./header/GlobalSearchModal";
import { BasicDetailsModal } from "@entities/transactions/forms/BasicDetailsModal";
import { initiateGoogleOAuth } from "@services/google/googleOauth";
import { useCurrentEmployee } from "../contexts/useCurrentEmployee";
import { filterSiderItem } from "./CustomSider.helpers";
import { ACCESS_GATES } from "@ds/config/accessGates";
import type { EmployeeRole } from "../types/enums";
import { useEntityNavigate } from "../utility/entityRoutes";

type MenuItem = NonNullable<MenuProps["items"]>[number];

const { Text } = Typography;
const { useToken } = theme;

/** Map sider menu keys to their visibility gate. Unlisted keys are always visible. */
/**
 * Convert Refine's `TreeMenuItem[]` to AntD's `MenuProps["items"]` shape
 * (the new declarative `items` prop, NOT the legacy `<Menu.Item>` children
 * which trigger AntD's deprecation warning + leak internal `eventKey`/`warnKey`
 * props onto the DOM).
 *
 * Leaves wrap their label in a `<Link to={route}>` so navigation is purely
 * router-driven; AntD's Menu only controls selection / open state. SubMenu
 * parents whose keyed children are all gated out collapse to null.
 *
 * Role gating: per-item via `filterSiderItem`. Skipped subtrees disappear
 * entirely so a parent with all-hidden children doesn't render an empty
 * SubMenu.
 */
function buildAntdMenuItems(
  items: TreeMenuItem[],
  hasRole: (...roles: EmployeeRole[]) => boolean,
  alertCount: number,
): MenuItem[] {
  const out: MenuItem[] = [];
  for (const item of items) {
    const key = String(item.key);
    if (!filterSiderItem(key, hasRole)) continue;
    const label = item.label ?? item.name;

    if (item.children && item.children.length > 0) {
      const children = buildAntdMenuItems(item.children, hasRole, alertCount);
      // Drop SubMenu if every child got filtered out.
      if (children.length === 0) continue;
      out.push({ key, icon: item.icon, label, children });
    } else {
      // Leaf — wrap label in <Link> if a route is declared. The logistics
      // load-alerts item carries a live red count badge of how many loads due
      // by the next working day are still missing a pre-load requirement.
      const isLoadAlerts = key.endsWith("logistics-load-alerts");
      const labelNode =
        isLoadAlerts && alertCount > 0 ? (
          <Space size={8} align="center">
            {label}
            <Badge count={alertCount} size="small" overflowCount={99} />
          </Space>
        ) : (
          label
        );
      out.push({
        key,
        icon: item.icon,
        label: item.route ? (
          <Link to={item.route} data-testid={`sider-link-${item.name}`}>{labelNode}</Link>
        ) : (
          labelNode
        ),
      });
    }
  }
  return out;
}

/** Props `ThemedLayout` passes to its `Sider` slot. Derived so it can't drift. */
type ThemedLayoutSiderProps = React.ComponentProps<
  NonNullable<React.ComponentProps<typeof ThemedLayout>["Sider"]>
>;

export const CustomSider: React.FC<ThemedLayoutSiderProps> = () => {
  const { data: identity } = useGetIdentity<{ email: string }>();
  const { mutate: logout } = useLogout();
  const { token } = useToken();
  const { hasRole } = useCurrentEmployee();
  const navigateToEntity = useEntityNavigate();
  // Live count for the load-alerts menu badge; polls so it stays fresh without
  // a manual reload. Only fetched for roles that can see the logistics area.
  const alertCount = useLogisticsAlertCount(
    hasRole(...ACCESS_GATES.logistics_dashboard_show),
  );
  const [searchOpen, setSearchOpen] = useState(false);
  // Collapsed state owned by Refine's ThemedLayoutContextProvider — same
  // hook that ThemedSider used internally. Persists across page changes;
  // ThemedLayout's mobile trigger flips mobileSiderOpen.
  const { siderCollapsed, setSiderCollapsed } = useThemedLayoutContext();

  // Collect menu items with tag: "new" for badge injection via CSS
  const { menuItems, selectedKey, defaultOpenKeys } = useMenu();
  const taggedKeys: string[] = [];
  const collectTagged = (items: TreeMenuItem[]) => {
    for (const item of items) {
      if (item.meta?.tag) taggedKeys.push(String(item.key));
      if (item.children?.length) collectTagged(item.children);
    }
  };
  collectTagged(menuItems);

  const badgeSelectors = taggedKeys.map(key =>
    `li[data-menu-id$="${key}"] > .ant-menu-title-content::after`
  ).join(",\n");
  const itemSelectors = taggedKeys.map(key =>
    `li[data-menu-id$="${key}"] > .ant-menu-title-content`
  ).join(",\n");
  const newBadgeCSS = taggedKeys.length > 0 ? `${itemSelectors} {
    display: flex;
    align-items: center;
  }
  ${badgeSelectors} {
    content: "";
    width: 6px;
    height: 6px;
    min-width: 6px;
    margin-left: 4px;
    border-radius: 50%;
    background: ${token.colorPrimary};
    display: inline-block;
  }` : "";

  const [createTxOpen, setCreateTxOpen] = useState(false);
  const createTransaction = () => setCreateTxOpen(true);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isMac = /Mac|iPhone|iPad/.test(navigator.platform);
      // Mac: ⌘+K, Windows/Linux: Ctrl+/ (Ctrl+K conflicts with Chrome address bar)
      if (isMac ? (e.metaKey && e.key === "k") : (e.ctrlKey && e.key === "/")) {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    const handler = () => setSearchOpen(true);
    window.addEventListener("open-global-search", handler);
    return () => window.removeEventListener("open-global-search", handler);
  }, []);

  return (
    <>
      <GlobalSearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onCreateTransaction={createTransaction}
        onCreateCompany={() => navigateToEntity("company", "new")}
      />
      <BasicDetailsModal
        transactionId={null}
        mode="create"
        open={createTxOpen}
        onClose={() => setCreateTxOpen(false)}
        onSaved={() => setCreateTxOpen(false)}
      />
      {newBadgeCSS && <style>{newBadgeCSS}</style>}
      <ConfigProvider
        theme={{
          // Scope these tokens to the sider subtree only. Refine's ThemedSider
          // hardcodes inline `style={{ backgroundColor: token.colorBgContainer,
          // borderRight: 1px solid token.colorBgElevated }}` (see
          // node_modules/@refinedev/antd/dist/index.mjs:1730), so to dark-theme
          // the sider correctly we need to feed those tokens, not just
          // Layout.siderBg.
          token: {
            colorBgContainer: EDITORIAL.sidebar,
            colorBgElevated:  EDITORIAL.sidebarSurface,
          },
          components: {
            Layout: {
              siderBg:      EDITORIAL.sidebar,
              lightSiderBg: EDITORIAL.sidebar,
              triggerBg:    EDITORIAL.sidebarSurface,
              triggerColor: EDITORIAL.sidebarText,
            },
            Menu: {
              // Sider Menu renders in LIGHT mode (Refine's ThemedSider doesn't
              // pass theme="dark" through). Override the light tokens for this
              // subtree so the dark navy sider renders correctly.
              itemBg:            "transparent",
              subMenuItemBg:     EDITORIAL.sidebar,
              itemColor:         EDITORIAL.sidebarText,
              itemHoverBg:       EDITORIAL.sidebarHover,
              itemHoverColor:    "#ffffff",
              itemSelectedBg:    EDITORIAL.sidebarActive,
              itemSelectedColor: "#ffffff",
              groupTitleColor:   EDITORIAL.sidebarGroup,
              iconSize:          14,
            },
          },
        }}
      >
      {/*
        Direct Layout.Sider mount — drops ThemedSider because its internal
        renderMenu() builds <Menu> with Menu.Item children (deprecated AntD
        v5 API). Even when the consumer provides a render() callback that
        ignores those children, the React element construction still fires
        the [antd: Menu] children-deprecated warning + the eventKey/warnKey
        DOM warnings. Owning the sider chrome ourselves eliminates the noise.

        Sized + positioned to match ThemedSider's behavior:
          - 240px expanded / 80px collapsed
          - fixed positioning + spacer div so page content reserves the width
          - bottom trigger button to toggle collapsed
      */}
      {/* Spacer reserves layout width when sider is fixed. */}
      <div style={{ width: siderCollapsed ? 80 : 240, transition: "all 0.2s", flexShrink: 0 }} />
      <Layout.Sider
        data-testid="app-sider"
        collapsible
        collapsed={siderCollapsed}
        onCollapse={setSiderCollapsed}
        width={240}
        collapsedWidth={80}
        breakpoint="lg"
        style={{
          backgroundColor: EDITORIAL.sidebar,
          borderRight: `1px solid ${EDITORIAL.sidebarSurface}`,
          position: "fixed",
          top: 0,
          left: 0,
          height: "100vh",
          zIndex: 999,
          display: "flex",
          flexDirection: "column",
        }}
        trigger={
          <Button
            type="text"
            style={{
              borderRadius: 0,
              height: "100%",
              width: "100%",
              backgroundColor: EDITORIAL.sidebarSurface,
              color: EDITORIAL.sidebarText,
            }}
            icon={siderCollapsed
              ? <RightOutlined style={{ color: token.colorPrimary }} />
              : <LeftOutlined  style={{ color: token.colorPrimary }} />}
          />
        }
      >
        {/* Logo */}
        <div
          style={{
            width: siderCollapsed ? 80 : 240,
            flexShrink: 0,
            padding: siderCollapsed ? "16px 4px" : "20px 12px",
            display: "flex", justifyContent: "center", alignItems: "center",
            backgroundColor: EDITORIAL.sidebarSurface,
            transition: "all 0.2s",
          }}
        >
          <img src="/logo-dark.png" alt="iooo ReUse" style={{ width: "100%", objectFit: "contain" }} />
        </div>
        {/* Body — search button + declarative Menu + bottom controls */}
        <div className="sider-scroll" style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, paddingTop: 8, overflow: "auto" }}>
          <Button
            type="text"
            icon={<SearchOutlined />}
            onClick={() => setSearchOpen(true)}
            style={{ width: "100%", textAlign: "left", padding: "0 24px", marginBottom: 4, color: EDITORIAL.sidebarText }}
          >
            {siderCollapsed ? null : "Szukaj"}
          </Button>
          <Menu
            mode="inline"
            inlineCollapsed={siderCollapsed}
            selectedKeys={selectedKey ? [selectedKey] : []}
            defaultOpenKeys={defaultOpenKeys}
            items={buildAntdMenuItems(menuItems, hasRole, alertCount)}
            style={{ borderRight: 0, background: "transparent" }}
          />
          <div style={{ flex: 1 }} />
          <div style={{ borderTop: `1px solid ${EDITORIAL.sidebarHover}`, padding: "8px 0" }}>
            {!siderCollapsed && identity?.email && (
              <div style={{ padding: "4px 24px 8px", display: "flex", alignItems: "center", gap: 8 }}>
                <UserOutlined style={{ color: EDITORIAL.sidebarTextDim, fontSize: FONT_SIZE.small }} />
                <Text ellipsis style={{ color: EDITORIAL.sidebarTextDim, fontSize: FONT_SIZE.small, flex: 1 }}>
                  {identity.email}
                </Text>
              </div>
            )}
            <Button
              type="text"
              icon={<GoogleOutlined />}
              onClick={() => initiateGoogleOAuth()}
              style={{ width: "100%", textAlign: "left", padding: "0 24px", color: EDITORIAL.sidebarText }}
            >
              {siderCollapsed ? null : "Google"}
            </Button>
            <Button
              type="text"
              icon={<LogoutOutlined />}
              onClick={() => logout()}
              style={{ width: "100%", textAlign: "left", padding: "0 24px", color: EDITORIAL.sidebarText }}
            >
              {siderCollapsed ? null : "Wyloguj"}
            </Button>
          </div>
        </div>
      </Layout.Sider>
      </ConfigProvider>
    </>
  );
};
